import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import ReactPlayer from "react-player";
import { gsap } from "gsap";
import { Col, Container, Row } from "react-bootstrap";
import Button from "./Button";
import { SafeHtmlParser } from "./SafeHtmlParser";

// Animation functions remain unchanged.
const animateText = (element) => {
	if (!element) return;
	element.style.opacity = "0";
	const originalHeight = element.offsetHeight;
	element.style.height = `${originalHeight}px`;

	const words = element.textContent.split(" ").map((word) => {
		const wrapper = document.createElement("div");
		wrapper.style.overflow = "hidden";
		wrapper.style.display = "inline-block";
		wrapper.style.marginRight = "0.25rem";
		const span = document.createElement("span");
		span.textContent = word;
		span.style.display = "inline-block";
		span.style.transform = "translateY(100%)";
		wrapper.appendChild(span);
		return wrapper;
	});

	element.textContent = "";
	words.forEach((word) => element.appendChild(word));
	const spans = element.querySelectorAll("span");
	gsap.set(spans, { y: "100%" });
	gsap.set(element, { opacity: 1 });
	gsap.to(spans, {
		y: "0%",
		duration: 0.6,
		stagger: 0.08,
		ease: "power3.out",
		delay: 0.2,
	});
};

const animateHeading = (element) => {
	if (!element) return;
	element.style.opacity = "0";
	const originalHeight = element.offsetHeight;
	element.style.height = `${originalHeight}px`;

	const words = element.textContent.split(" ").map((word) => {
		const wrapper = document.createElement("div");
		wrapper.style.overflow = "hidden";
		wrapper.style.display = "inline-block";
		wrapper.style.marginRight = "0.9rem";
		const span = document.createElement("span");
		span.textContent = word;
		span.style.display = "inline-block";
		span.style.transform = "translateY(100%)";
		wrapper.appendChild(span);
		return wrapper;
	});
	element.textContent = "";
	words.forEach((word) => element.appendChild(word));
	const spans = element.querySelectorAll("span");
	gsap.set(spans, { y: "100%" });
	gsap.set(element, { opacity: 1 });
	gsap.to(spans, {
		y: "0%",
		duration: 0.6,
		stagger: 0.08,
		ease: "power3.out",
		delay: 0.2,
	});
};

const Header1 = ({
	heading,
	body,
	button1,
	button2,
	backgroundImage,
	mediaType,
	video,
	videoDesktop,
	overlayColour,
}) => {
	const bgImage = getImage(backgroundImage);
	const headingRef = useRef(null);
	const bodyRef = useRef(null);
	const buttonsRef = useRef(null);
	const containerRef = useRef(null);

	// Determine if mobile and set media container height.
	const [isMobile, setIsMobile] = useState(false);
	const [mediaHeight, setMediaHeight] = useState("80vh");
	const [newOpac, setNewOpac] = useState(0);

	useEffect(() => {
		const handleResize = () => {
			const width = window.innerWidth;
			setIsMobile(width < 768);
			if (width < 768) {
				// Use aspect ratio container on mobile.
				setMediaHeight(null);
			} else if (width < 992) {
				setMediaHeight("60vh");
			} else {
				setMediaHeight("80vh");
			}
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Reset inline styles when switching between mobile and desktop.
	useEffect(() => {
		if (headingRef.current) {
			headingRef.current.style.opacity = "1";
			headingRef.current.style.height = "auto";
		}
		if (bodyRef.current) {
			bodyRef.current.style.opacity = "1";
			bodyRef.current.style.height = "auto";
		}
		if (buttonsRef.current) {
			const buttons = buttonsRef.current.children;
			for (let i = 0; i < buttons.length; i++) {
				buttons[i].style.opacity = "1";
				buttons[i].style.transform = "translateY(0%)";
			}
		}
	}, [isMobile]);

	useLayoutEffect(() => {
		setTimeout(() => {
			gsap.to(containerRef.current, {
				opacity: 1,
				duration: 1,
				onComplete: () => {
					// Remove the inline style so it doesn't override on resize.
					setNewOpac(1);
				},
			});
			animateHeading(headingRef.current);
			animateText(bodyRef.current);
			if (buttonsRef.current) {
				const buttons = buttonsRef.current.children;
				gsap.set(buttons, { y: "100%", opacity: 0 });
				gsap.to(buttons, {
					y: "0%",
					opacity: 1,
					duration: 0.6,
					stagger: 0.1,
					ease: "power3.out",
					delay: 1,
				});
			}
		}, 50);
	}, []);

	// Shared content markup.
	const contentMarkup = (
		<Container
			ref={containerRef}
			style={{ opacity: newOpac }}
			className={isMobile ? "py-4" : "h-100"}
		>
			<Row
				className={
					isMobile
						? "justify-content-center text-center"
						: "justify-content-center align-items-center pb-5 pb-md-0 h-100 text-center"
				}
			>
				<Col className="text-white" lg={8}>
					<h1 style={{ opacity: 0 }} className="display-1" ref={headingRef}>
						{heading}
					</h1>
					<div className="pt-2 pb-1" style={{ opacity: 0 }} ref={bodyRef}>
						<SafeHtmlParser htmlContent={body} />
					</div>
					<div
						className="d-flex justify-content-center gap-3 mt-3"
						ref={buttonsRef}
					>
						{button1 && (
							<Button
								colour="#FEDE34"
								text={button1.title}
								link={button1.url}
								isOutline={false}
								textColor="#fff"
							/>
						)}
						{button2 && (
							<Button
								colour="#FEDE34"
								text={button2.title}
								link={button2.url}
								isOutline={true}
								outlineTextColor="#fff"
								hoverColor="#000"
							/>
						)}
					</div>
				</Col>
			</Row>
		</Container>
	);

	// Container style:
	// Desktop: fixed height (mediaHeight) with overflow hidden.
	// Mobile: use an aspect ratio container so height is auto-calculated.
	const mediaWrapperStyle = isMobile
		? { position: "relative", width: "100%", aspectRatio: "16 / 9" }
		: {
				width: "100%",
				height: mediaHeight,
				position: "relative",
				overflow: "hidden",
		  };

	if (!isMobile) {
		// Desktop layout: inline ReactPlayer with config.
		return (
			<div
				className="media-wrapper position-relative"
				style={mediaWrapperStyle}
			>
				{mediaType.toLowerCase() === "video" ? (
					<div className="video-container">
						<ReactPlayer
							url={videoDesktop || video}
							playing
							playsinline
							muted
							loop
							width="100%"
							height="100%"
							style={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
								pointerEvents: "none",
							}}
							config={{
								file: {
									attributes: {
										style: {
											objectFit: "cover",
											width: "100%",
											height: "100%",
										},
									},
								},
							}}
						/>
					</div>
				) : (
					<BgImage
						style={{ width: "100%", height: "100%" }}
						image={bgImage}
						className="position-relative"
					/>
				)}
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						background: overlayColour, // adjust opacity/color as needed
						zIndex: 1,
					}}
				/>
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						zIndex: 2,
					}}
				>
					{contentMarkup}
				</div>
			</div>
		);
	}

	// Mobile layout: use a different key and no config prop.
	return (
		<>
			<div className="media-wrapper bg-primary" style={mediaWrapperStyle}>
				{mediaType.toLowerCase() === "video" ? (
					<ReactPlayer
						key="mobile"
						url={video}
						playing
						playsinline
						loop
						muted
						width="100%"
						height="100%"
						style={{
							position: "relative",
							objectFit: "cover",
						}}
						config={{}}
					/>
				) : (
					<GatsbyImage
						image={bgImage}
						alt="Header Image"
						style={{ width: "100%", height: "100%" }}
					/>
				)}
			</div>
			<div className="py-4 bg-primary">{contentMarkup}</div>
		</>
	);
};

export default Header1;
